import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Formik, Form } from "formik";
import {
  useGetUserStandardPlanMutation,
  useCreateNewPlanMutation,
} from "services/user-service";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import config from "../../../config";

const PLAN_DETAILS = {
  LEADS: {
    MONTHLY: {
      STARTER: {
        price: "$49",
        priceId: config.LEADS_MONTHLY_STARTER_PRICE_ID,
      },
      GROWTH: {
        price: "$99",
        priceId: config.LEADS_MONTHLY_GROWTH_PRICE_ID,
      },
      INFINITY: {
        price: "$199",
        priceId: config.LEADS_MONTHLY_INFINITY_PRICE_ID,
      },
    },
    YEARLY: {
      STARTER: {
        price: "$49",
        priceId: config.LEADS_YEARLY_STARTER_PRICE_ID,
      },
      GROWTH: {
        price: "$99",
        priceId: config.LEADS_YEARLY_GROWTH_PRICE_ID,
      },
      INFINITY: {
        price: "$199",
        priceId: config.LEADS_YEARLY_INFINITY_PRICE_ID,
      },
    },
    FEATURES: {
      STARTER: [
        "Lead Database Access",
        "Basic Lead Scoring",
        "Export Capabilities",
      ],
      GROWTH: [
        "Advanced Lead Analytics",
        "Custom Lead Filters",
        "Team Collaboration",
      ],
      INFINITY: [
        "Unlimited Lead Exports",
        "AI-Powered Lead Scoring",
        "Priority Support",
      ],
    },
  },
  OUTREACH: {
    MONTHLY: {
      STARTER: {
        price: "$39",
        priceId: config.OUTREACH_MONTHLY_STARTER_PRICE_ID,
      },
      GROWTH: {
        price: "$99",
        priceId: config.OUTREACH_MONTHLY_GROWTH_PRICE_ID,
      },
      INFINITY: {
        price: "$199",
        priceId: config.OUTREACH_MONTHLY_INFINITY_PRICE_ID,
      },
    },
    YEARLY: {
      STARTER: {
        price: "$39",
        priceId: config.OUTREACH_YEARLY_STARTER_PRICE_ID,
      },
      GROWTH: {
        price: "$99",
        priceId: config.OUTREACH_YEARLY_GROWTH_PRICE_ID,
      },
      INFINITY: {
        price: "$199",
        priceId: config.OUTREACH_YEARLY_INFINITY_PRICE_ID,
      },
    },
    FEATURES: {
      STARTER: [
        "Basic Campaign Management",
        "Email Templates",
        "Basic Analytics",
      ],
      GROWTH: [
        "Advanced Campaign Tools",
        "A/B Testing",
        "Automation Workflows",
      ],
      INFINITY: [
        "Custom Integration",
        "Advanced Automation",
        "Dedicated Support",
      ],
    },
  },
};

const UserNewPlan = ({ open, onClose, allWorkspaces, userId }) => {
  const navigate = useNavigate();
  const [getUserStandardPlan] = useGetUserStandardPlanMutation();
  const [createNewPlan] = useCreateNewPlanMutation();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPlans, setCurrentPlans] = useState({
    LEADS: null,
    OUTREACH: null,
  });

  const getPlansForType = (type, isYearly) => {
    const planPeriod = isYearly ? "YEARLY" : "MONTHLY";
    return [
      {
      id: 'FREE',
      title: 'Free',
      priceId: 'free',
     },
      {
        id: "STARTER",
        title: "Starter",
        priceId: PLAN_DETAILS[type][planPeriod].STARTER.priceId,
      },
      {
        id: "GROWTH",
        title: "Growth",
        priceId: PLAN_DETAILS[type][planPeriod].GROWTH.priceId,
      },
      {
        id: "INFINITY",
        title: "Infinity",
        priceId: PLAN_DETAILS[type][planPeriod].INFINITY.priceId,
      },
    ];
  };

  // Set default workspace only once
  useEffect(() => {
    if (allWorkspaces?.length > 0 && !selectedWorkspace) {
      setSelectedWorkspace(allWorkspaces[0]);
    }
  }, [allWorkspaces]);

  useEffect(() => {
    if (!selectedWorkspace?._id) return;

    const fetchCurrentPlan = async () => {
      try {
        const userPlan = await getUserStandardPlan(
          selectedWorkspace._id
        ).unwrap();

        // Get both leads and outreach plans
        const leadsPlanId = userPlan?.subscription?.leads?.planId;
        const outreachPlanId = userPlan?.subscription?.sendingWarmup?.planId;

        // Set current plans for both types
        const leadsPlans = getPlansForType("LEADS", false);
        const outreachPlans = getPlansForType("OUTREACH", false);

        setCurrentPlans({
          LEADS: leadsPlanId
            ? leadsPlans.find((p) => p.priceId === leadsPlanId)
            : leadsPlans[0],
          OUTREACH: outreachPlanId
            ? outreachPlans.find((p) => p.priceId === outreachPlanId)
            : outreachPlans[0],
        });

        // Always select LEADS plan initially
        const initialPlan = leadsPlanId
          ? leadsPlans.find((p) => p.priceId === leadsPlanId)
          : leadsPlans[0];
        setSelectedPlan(initialPlan);
      } catch (error) {
        toast.error("Failed to fetch current plan");

        // Default to LEADS if error occurs
        const defaultPlan = getPlansForType("LEADS", false)[0];
        setSelectedPlan(defaultPlan);
        setCurrentPlans({ LEADS: defaultPlan, OUTREACH: null });
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentPlan();
  }, [selectedWorkspace, getUserStandardPlan]);

  const handleFormSubmit = async (values, { setSubmitting }) => {
    if (!values.selectedPlan) {
      toast.error("Please select a plan");
      setSubmitting(false);
      return;
    }

    const workspaceId = selectedWorkspace?._id;
    const planId = values.selectedPlan.priceId;

    // if (!planId) {
    //   toast.error("Invalid plan configuration");
    //   setSubmitting(false);
    //   return;
    // }

    try {
      const { message } = await createNewPlan({
        user: userId,
        selectedWorkspace: workspaceId,
        planId,
        isYearly: values.isYearly,
        planType: values.planType,
      }).unwrap();

      // Update current plans state
      setCurrentPlans((prev) => ({
        ...prev,
        [values.planType]: values.selectedPlan,
      }));

      toast.success(message || "Plan updated successfully");
      onClose();
      navigate(0);
    } catch (error) {
      toast.error(error.data.message || "Failed to update plan");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Plan</DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          selectedWorkspace,
          selectedPlan,
          isYearly: false,
          planType: "LEADS",
        }}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          setFieldValue,
          handleBlur,
          isSubmitting,
          touched,
          errors,
        }) => {
          //   console.log("values ---- ", values);
          return (
            <Form>
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="h5">
                  Current {values.planType} Plan:{" "}
                  <Typography component="span" variant="body2">
                    {currentPlans[values.planType]?.title || ""}
                  </Typography>
                </Typography>
              </Box>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Plan Type
                      </Typography>
                      <ToggleButtonGroup
                        value={values.planType}
                        exclusive
                        onChange={(e, newValue) => {
                          if (newValue) {
                            if (!newValue) return;
                            setFieldValue("planType", newValue);
                            // Set the current plan for the selected type
                            const currentPlan =
                              currentPlans[newValue] ||
                              getPlansForType(newValue, values.isYearly)[0];
                            setFieldValue("selectedPlan", currentPlan);
                          }
                        }}
                      >
                        <ToggleButton value="LEADS">Leads</ToggleButton>
                        <ToggleButton value="OUTREACH">Outreach</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="isYearly"
                          checked={values.isYearly}
                          onChange={(event) => {
                            setFieldValue("isYearly", event.target.checked);
                            const plans = getPlansForType(
                              values.planType,
                              event.target.checked
                            );
                            setFieldValue("selectedPlan", plans[0]);
                            setSelectedPlan(plans[0]);
                          }}
                          onBlur={handleBlur}
                        />
                      }
                      label="Yearly Plan"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={allWorkspaces}
                      getOptionLabel={(option) => option.name || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Workspace"
                          variant="outlined"
                          onBlur={handleBlur}
                          error={
                            touched.selectedWorkspace &&
                            Boolean(errors.selectedWorkspace)
                          }
                          helperText={
                            touched.selectedWorkspace &&
                            errors.selectedWorkspace
                          }
                        />
                      )}
                      onChange={(event, newValue) => {
                        setSelectedWorkspace(newValue);
                        setFieldValue("selectedWorkspace", newValue);
                      }}
                      value={selectedWorkspace}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={getPlansForType(
                        values.planType,
                        values.isYearly
                      )}
                      getOptionLabel={(option) => option.title || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Plan"
                          variant="outlined"
                          onBlur={handleBlur}
                          error={
                            touched.selectedPlan && Boolean(errors.selectedPlan)
                          }
                          helperText={
                            touched.selectedPlan && errors.selectedPlan
                          }
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Typography variant="subtitle1">
                            {option.title}
                          </Typography>
                        </li>
                      )}
                      onChange={(event, newValue) => {
                        setFieldValue("selectedPlan", newValue);
                      }}
                      value={values.selectedPlan}
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  Update Plan
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default UserNewPlan;
